module Widgets.Form.Class where

import Common

import API.Account (AccountInfo, AccountType(..))
import API.Autofill (AutofillArgs, initAutofillArgs)
import API.Document (Document)
import Concur.Core (Widget)
import Concur.React (HTML)
import Concur.React.Props (ReactProps)
import Concur.React.Props as P
import Data.Argonaut (class DecodeJson, Json, decodeJson)
import Data.Argonaut as J
import Data.String (Pattern(..), contains)
import Error (wrapJsonError')
import GraphQL.Shape (QueryField)
import GraphQL.Type (dummyId)
import Widgets.Form.Access (FieldAccessLevel, FormAccess, canUpdate, canView, fullFormAccess, hasVisibleDescendant, resolveFieldAccess)

-- | Represents global rendering context passed to `toWidget`.
-- |
-- | For now this doesn't do much, but it will be necessary in the future to
-- | implement conditional behavior.
type FormContext =
    { parentIds :: String
    -- ^ All parent form IDs joined by `.` separators. Used by children of
    -- ^ sub-forms to ensure unique HTML IDs so that `<label>` works correctly.
    , curIndex :: Int
    -- ^ Index of the widget currently being rendered within the most immediate
    -- list widget containing it. If none exists, this will be 0.
    , isAutofilling :: Boolean
    -- ^ Used for form widgets that need to behave differently based on whether
    -- we're autofilling or loading a form.
    , autofillArgs :: AutofillArgs
    , account :: AccountInfo
    , doc :: Maybe Document
    , access :: FormAccess
    }

initFormContext :: AccountInfo -> Document -> FormContext
initFormContext account doc =
    { parentIds: ""
    , curIndex: 0
    , doc: Just doc
    , isAutofilling: false
    , autofillArgs: initAutofillArgs doc.formId doc.student.id
    , account
    , access: fullFormAccess
    }

blankFormContext :: FormContext
blankFormContext =
    { parentIds: ""
    , curIndex: 0
    , doc: Nothing
    , isAutofilling: false
    , autofillArgs: initAutofillArgs dummyId dummyId
    , account: { type: TeacherAccount, student: Nothing }
    , access: fullFormAccess
    }

-- | Generates a unique string ID for a widget by prefixing the given widget ID
-- | with that of its parents.
fieldId :: FormContext -> String -> String
fieldId { parentIds: "" } id = id
fieldId { parentIds } id = parentIds <> "." <> id

-- | Generates Tailwind classes for widgets, taking into account that form
-- | designers will want to override things like width.
fieldClass :: forall r. { cls :: String | r } -> String
fieldClass { cls: "" } = "flex flex-wrap col-span-12"
fieldClass { cls } =
    if contains (Pattern "col-span-") cls
    then "flex flex-wrap " <> cls
    else "flex flex-wrap col-span-12 " <> cls

-- | Creates a new form context with the given string appended to the chain of `parentIds`.
addParentId :: FormContext -> String -> FormContext
addParentId fc id = fc { parentIds = fieldId fc id }

fieldAccessLevel :: FormContext -> String -> FieldAccessLevel
fieldAccessLevel fc id = resolveFieldAccess fc.access $ fieldId fc id

fieldCanView :: FormContext -> String -> Boolean
fieldCanView fc = canView <<< fieldAccessLevel fc

fieldCanUpdate :: FormContext -> String -> Boolean
fieldCanUpdate fc = canUpdate <<< fieldAccessLevel fc

fieldAccessProps :: forall a. FormContext -> String -> Array (ReactProps a)
fieldAccessProps fc id
    | fieldCanUpdate fc id = []
    | otherwise = [ P.disabled true, P.tabIndex (-1) ]

fieldHasVisibleDescendant :: FormContext -> String -> Boolean
fieldHasVisibleDescendant fc id = hasVisibleDescendant fc.access $ fieldId fc id

-- | Class for form widgets.
class FormWidget state where
    -- | Gets this widget's ID.
    getId :: state -> String
    -- | Renders this widget with a given render context and widget state.
    toWidget :: FormContext -> state -> Widget HTML (FormWidgetResult state)

    -- | Returns true if this widget could have any children. Widgets that are
    -- | leaves will be debounced.
    isLeaf :: state -> Boolean
    -- | Returns this widget represented as a GraphQL field.
    asField :: state -> Maybe QueryField

    -- | Extracts the current data in this widget.
    getData :: FormContext -> state -> Json
    -- | Sets the current data in this widget from JSON.
    -- | `Left` may be returned to indicate invalid data.
    setData :: FormContext -> state -> Json -> Either ErrorWrapper state

    -- | Returns the access-controlled paths that correspond to editable form
    -- | interactions for this widget's current tree.
    fieldPaths :: FormContext -> state -> Array String

formWidgetState :: forall s. s -> FormWidgetResult s
formWidgetState state = FormWidgetResult { state, action: Nothing }

formWidgetAction :: forall s. s -> FormWidgetAction -> FormWidgetResult s
formWidgetAction state act = FormWidgetResult { state, action: Just act }

-- | Use when `getData` is not relevent for a widget.
-- |
-- | No data will not be saved to the backend.
noGetData :: forall state. FormContext -> state -> Json
noGetData _ _ = J.jsonNull

-- | Use when `setData` is not relevent for a widget.
-- |
-- | The widget will ignore any JSON data loaded into it.
noSetData :: forall state. FormContext -> state -> Json -> Either ErrorWrapper state
noSetData _ s _ = Right s

-- | Result returned by `toWidget`.
-- |
-- | Includes the new state of the widget and an optional action to run.
-- |
-- | Use `formWidgetState` and `formWidgetAction` instead of constructing this
-- | manually.
data FormWidgetResult s = FormWidgetResult { state :: s, action :: Maybe FormWidgetAction }

instance fwrFunctor :: Functor FormWidgetResult where
    map f (FormWidgetResult r) = FormWidgetResult r { state = f r.state }

-- | Actions which can be triggered by a widget to affect the entire form.
data FormWidgetAction
    -- | Load data from some aff (for example, loading autofill data from the
    -- | backend) into the form.
    = FWAutoFill AutofillArgs

-- | Helper for `setData` which handles all the json parsing and wraps an inner
-- | function which just deals with the parsed value.
setFromJson :: forall state val. FormWidget state => DecodeJson val =>
    state -> (val -> Either ErrorWrapper state) -> Json -> Either ErrorWrapper state
setFromJson _ inner json = do
    val <- wrapJsonError' json $ decodeJson json
    inner val
